import React, { useState } from 'react';

import { Alert, AlertTitle, Typography } from '@mui/material';
import { useCurrentUserId } from 'src/gql';
import { makeStyles } from 'tss-react/mui';

import {
  getHasReviewers,
  getIsReviewerRow,
  getReadyToCompleteText,
  getRequesterAssignment,
  getYourNextStep,
} from './task-review-helpers';
import type { TaskWithComments } from './use-task-panel-state';

const useStyles = makeStyles()(theme => ({
  nextStep: {
    borderRadius: 8,
  },
  nextStepDetail: {
    color: theme.appColors.iconsType,
    fontSize: 14,
    lineHeight: '20px',
  },
  nextStepTitle: {
    color: theme.appColors.strongType,
    fontSize: 16,
    fontWeight: 600,
    lineHeight: '24px',
    marginBottom: theme.spacing(0.25),
  },
}));

interface TaskNextStepProps {
  documentCount: number;
  isExternalView: boolean;
  isPreview: boolean;
  task: TaskWithComments;
}

/**
 * "Your next step", built from the task's state. An external reads the owner's firm; an internal
 * reviewer reads the person who asked them. An internal assignee has the task-page banner instead.
 */
export function TaskNextStep({
  documentCount,
  isExternalView,
  isPreview,
  task,
}: TaskNextStepProps): JSX.Element | null {
  const { classes } = useStyles();
  const nextStep = useTaskNextStep({ documentCount, isExternalView, isPreview, task });
  // Dismissing hides this step only; the alert returns when the task moves to a new one.
  const [dismissedTitle, setDismissedTitle] = useState<string | null>(null);
  if (!nextStep || nextStep.title === dismissedTitle) return null;

  return (
    <Alert
      className={classes.nextStep}
      severity={nextStep.severity}
      onClose={() => setDismissedTitle(nextStep.title)}
    >
      <AlertTitle className={classes.nextStepTitle}>{nextStep.title}</AlertTitle>
      <Typography className={classes.nextStepDetail}>{nextStep.detail}</Typography>
    </Alert>
  );
}

/** The viewer's next step on this task, or null when an internal assignee has the banner. */
export function useTaskNextStep({
  documentCount,
  isExternalView,
  isPreview,
  task,
}: TaskNextStepProps): ReturnType<typeof getYourNextStep> | null {
  const currentUserId = useCurrentUserId();
  const ownAssignment = getRequesterAssignment(task.assignments, currentUserId);
  // A preview has no assignee of its own, so it shows the first external's step.
  const assignment = isPreview
    ? (task.assignments.find(item => item.isExternal) ?? null)
    : ownAssignment;
  const isInternalAssignee = !isExternalView && assignment && !getIsReviewerRow(assignment);
  const askerName = isExternalView ? null : assignment?.reviewRequestedBy?.displayName;
  const nextStep = getYourNextStep({
    assignment,
    documentCount,
    hasReviewers: getHasReviewers(task.assignments),
    ownerName: askerName || task.ownerName,
    readyToCompleteText: getReadyToCompleteText(task.assignments, { isExternalView }),
    status: task.status,
  });
  return isInternalAssignee ? null : nextStep;
}
